import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { isNil, isUndefined } from 'lodash-es';
import { useTranslation } from 'react-i18next';

import { useAppDispatch } from '@/store';
import { authenticationPasswordReset, authenticationPasswordResetRequest } from '@/store/slices/authentication';
import { isEmailValid } from '@/utils/strings';

import { Text } from '../../components/Text';
import { Divider } from '../../components/Divider';
import { useLanguageSwitcher } from '../../hooks/useLanguageSwitcher';
import { AuthLogo } from '../login/components/AuthLogo';
import { LanguagePicker } from '../login/components/LanguagePicker';
import { useAuthCardMorph } from '../login/useAuthCardMorph';
import { useAuthInputFocus } from '../login/useAuthInputFocus';
import { scorePassword } from '../../utils/password-strength';
import { ResetPassword } from './reset-password.styles';

// Request half: enter email -> `form` -> `sent`. Redemption half (arrived via the emailed
// `?code=` link): set a new password -> `set` -> `done`. The two halves share the page shell.
type ResetLayout = 'form' | 'sent' | 'set' | 'done';

/**
 * `/reset-password` (SPB-480): forgot-password / set-password request flow.
 *
 * Hand-written, strictly-typed auth sibling of `/login` and `/register`. It reuses their
 * shared pieces (AuthLogo, LanguagePicker, useAuthCardMorph, useAuthInputFocus) and the
 * `LOGIN` design language. The flow: enter an email, request a reset/set-password link
 * (`authenticationPasswordResetRequest`), then swap to a confirmation view. Rendered
 * full-bleed OUTSIDE the account chrome (see `router.tsx`) and NOT auth-gated: it is a
 * screen an unauthenticated visitor must be able to reach, linked from `/login`'s
 * "Forgot password?".
 *
 * STATUS: this page owns BOTH halves. Request half (`form` -> `sent`): enter an email, request a
 * reset link. Redemption half (`set` -> `done`, reached via the emailed `?code=` link): set a new
 * password, then a confirmation that sends the user to `/login`. The redemption deliberately does
 * NOT auto-sign-in (setting a password from an emailed link must not grant a session to whoever
 * holds the link, and the BE in fact invalidates all sessions on a reset); the owner
 * re-authenticates with the new password. Both backend endpoints are the shipped
 * micros-wl_owner_hub contract (`POST /auth/forgot-password` with `{ email }`, `POST
 * /auth/reset-password` with `{ code, new_password }`). The BE emails the link as
 * `{ownerHubFrontUrl}/reset_password?code=` (underscore) — so this page is dual-mounted at both
 * `/reset_password` and `/reset-password` (see `router.tsx`). On any reset failure the FE shows one
 * generic "invalid or expired" notice (no error-code branching; the copy covers both BE codes).
 */
const PageResetPassword = (): React.ReactElement => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { languages, currentLanguage, onChangeLanguage } = useLanguageSwitcher();
  const { innerRef, cardStyle } = useAuthCardMorph();

  // The emailed reset link lands here as `/reset_password?code=…` (the BE notification template).
  // A present, non-empty code opens the set-new-password view; its absence keeps the request form.
  // `?code=` with no value yields '' and counts as absent.
  const [searchParams, setSearchParams] = useSearchParams();
  const code = searchParams.get('code');
  const hasCode = !isNil(code) && code.length > 0;

  const [layout, setLayout] = React.useState<ResetLayout>(hasCode ? 'set' : 'form');
  const [email, setEmail] = React.useState<string | undefined>();
  const [submitting, setSubmitting] = React.useState<boolean>(false);
  // Set-new-password (redemption) state. `password` is shared with neither the email field nor
  // the request flow; `resetError` drives the single generic failure notice.
  const [password, setPassword] = React.useState<string>('');
  const [showPassword, setShowPassword] = React.useState<boolean>(false);
  const [resetError, setResetError] = React.useState<boolean>(false);
  const strength = React.useMemo(() => scorePassword(password), [password]);
  // Autofocuses the email field on the form view, suppressing the autofill popup on the
  // programmatic focus (see useAuthInputFocus). Spread inputProps on the input.
  const { emailRef, inputProps } = useAuthInputFocus(layout === 'form' && !submitting);

  const handleChange = React.useCallback<React.ChangeEventHandler<HTMLInputElement>>((event) => {
    setEmail(event.target.value);
  }, []);

  const isValid = React.useMemo<boolean>(() => isEmailValid(email), [email]);

  // Guards a double-click from firing two requests while the first is in flight.
  const submittingRef = React.useRef(false);

  const handleSubmit = React.useCallback<React.FormEventHandler<HTMLFormElement>>(
    (event) => {
      event.preventDefault();

      if (isUndefined(email) || !isValid || submittingRef.current) {
        return;
      }

      submittingRef.current = true;
      setSubmitting(true);

      void dispatch(authenticationPasswordResetRequest({ email }))
        .unwrap()
        .then(() => {
          // Always the same confirmation, whether or not the email has an account, so the
          // page never signals account existence (no enumeration). See the thunk JSDoc.
          setLayout('sent');
        })
        .catch(() => {
          // Request failed (e.g. network). Leave the form interactive so the user can retry.
        })
        .finally(() => {
          submittingRef.current = false;
          setSubmitting(false);
        });
    },
    [dispatch, email, isValid],
  );

  // Back from the confirmation view to the request form.
  const handleBackToForm = React.useCallback(() => {
    setLayout('form');
  }, []);

  const handlePasswordChange = React.useCallback<React.ChangeEventHandler<HTMLInputElement>>((event) => {
    setPassword(event.target.value);
    // Editing the password clears a prior failure so the user can retry (e.g. after a transient
    // network error), mirroring /register. A truly dead token just re-fails on resubmit, which
    // re-shows the notice + "Request a new link".
    setResetError(false);
  }, []);

  const canSubmitPassword = password.length >= 8 && !submitting;

  // Redemption submit: set the new password via the emailed token, then swap to the `done`
  // confirmation. NOT an auth event (no sign-in, no session touch); on success the user goes to
  // `/login` to authenticate with the new password. Any failure (invalid/expired code) shows one
  // generic notice and leaves the form interactive.
  const handlePasswordSubmit = React.useCallback<React.FormEventHandler<HTMLFormElement>>(
    (event) => {
      event.preventDefault();

      if (isNil(code) || password.length < 8 || submittingRef.current) {
        return;
      }

      submittingRef.current = true;
      setResetError(false);
      setSubmitting(true);

      void dispatch(authenticationPasswordReset({ code, new_password: password }))
        .unwrap()
        .then(() => {
          // Drop the (now consumed) code from the URL so it does not linger in history, and so a
          // refresh on the confirmation lands on the request form rather than re-opening the set
          // view with a dead code.
          setSearchParams({}, { replace: true });
          setLayout('done');
        })
        .catch(() => {
          setResetError(true);
        })
        .finally(() => {
          submittingRef.current = false;
          setSubmitting(false);
        });
    },
    [dispatch, code, password, setSearchParams],
  );

  // "Request a new link" from the error state: drop the dead token from the URL (replace, so
  // browser-back cannot restore it), clear the field, and return to the request form.
  const handleRequestNewLink = React.useCallback(() => {
    setSearchParams({}, { replace: true });
    setResetError(false);
    setPassword('');
    setLayout('form');
  }, [setSearchParams]);

  // Root class is `nova-reset-password` (non-colliding), same reason register uses
  // `nova-register`: keeps this page fully self-styled.
  return (
    <ResetPassword className="nova-reset-password">
      <div className="page">
        <div className="main">
          <div className="header">
            <AuthLogo />
          </div>

          <div className="body" style={cardStyle}>
            <div className="body-inner" ref={innerRef}>
              <div className="layout">
                {layout === 'form' ? (
                  <div className="wrapper form-view">
                    <form className="sign-in-form" onSubmit={handleSubmit}>
                      <div className="heading">
                        <Text className="title" text="Reset your password" />
                        <Text
                          className="subtitle"
                          text="Enter your email and we will send you a link to set a new password."
                        />
                      </div>

                      <div className="input">
                        <input
                          ref={emailRef}
                          type="email"
                          name="email"
                          autoComplete="email"
                          placeholder={t('Enter your email')}
                          value={email ?? ''}
                          onChange={handleChange}
                          {...inputProps}
                        />
                      </div>

                      <div className="cta">
                        <button
                          type="submit"
                          className={`button${isValid && !submitting ? '' : ' disabled'}`}
                          disabled={!isValid || submitting}
                        >
                          <Text className="text" text="Send reset link" />
                          <i className="bi bi-arrow-right icon" aria-hidden="true" />
                        </button>
                      </div>
                    </form>

                    <Divider variant="styled" className="auth-divider" />

                    <div className="footer">
                      <Link className="link" to="/login">
                        <Text text="Back to sign in" />
                      </Link>
                    </div>
                  </div>
                ) : layout === 'sent' ? (
                  <div className="wrapper sent-view">
                    <div className="heading">
                      <button type="button" className="back" onClick={handleBackToForm} aria-label={t('Back')}>
                        <i className="bi bi-arrow-left" aria-hidden="true" />
                      </button>

                      <div className="heading-copy">
                        <Text className="title" text="Check your email" />
                        <Text
                          className="subtitle"
                          text="If an account exists for this email, a reset link is on the way."
                        />
                      </div>
                    </div>

                    <div className="email-chip">
                      <i className="bi bi-envelope icon" aria-hidden="true" />
                      <Text className="email" text={email ?? ''} />
                    </div>

                    <Divider variant="styled" className="auth-divider" />

                    <div className="footer">
                      <Link className="link" to="/login">
                        <Text text="Back to sign in" />
                      </Link>
                    </div>
                  </div>
                ) : layout === 'set' ? (
                  <div className="wrapper form-view">
                    <form className="sign-in-form" onSubmit={handlePasswordSubmit}>
                      <div className="heading">
                        <Text className="title" text="Set a new password" />
                        <Text className="subtitle" text="Choose a new password for your account." />
                      </div>

                      {resetError ? (
                        <div className="link-notice" role="alert">
                          <i className="bi bi-exclamation-circle-fill notice-icon" aria-hidden="true" />
                          <div className="notice-copy">
                            <Text className="notice-title" text="This reset link is invalid or has expired" />
                            <Text className="notice-hint" text="Request a new link below." />
                          </div>
                        </div>
                      ) : null}

                      <div className="input">
                        <div className="password-input">
                          <input
                            type={showPassword ? 'text' : 'password'}
                            name="new-password"
                            autoComplete="new-password"
                            placeholder={t('Create a password')}
                            value={password}
                            onChange={handlePasswordChange}
                            maxLength={128}
                          />
                          <button
                            type="button"
                            className="eye-toggle"
                            aria-label={t(showPassword ? 'Hide password' : 'Show password')}
                            onClick={() => setShowPassword((v) => !v)}
                          >
                            <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`} aria-hidden="true" />
                          </button>
                        </div>

                        {/* Strength meter: reserved height, hidden until the first keystroke, uniform
                          colour per state. Bars are decorative (aria-hidden); the caption carries the
                          meaning. Same behaviour as register's meter. */}
                        <div
                          className={`password-strength strength-${strength.level}${
                            password.length > 0 ? ' is-visible' : ''
                          }`}
                        >
                          <div className="strength-bars" aria-hidden="true">
                            <span className={`bar${strength.score >= 1 ? ' filled' : ''}`} />
                            <span className={`bar${strength.score >= 2 ? ' filled' : ''}`} />
                            <span className={`bar${strength.score >= 3 ? ' filled' : ''}`} />
                          </div>
                          <span className="strength-caption" aria-live="polite">
                            {password.length === 0 ? null : strength.level === 'too-short' ? (
                              <Text text="Use at least 8 characters." />
                            ) : strength.level === 'weak' ? (
                              <Text text="Weak password" />
                            ) : strength.level === 'fair' ? (
                              <Text text="Fair password" />
                            ) : (
                              <Text text="Strong password" />
                            )}
                          </span>
                        </div>
                      </div>

                      <div className="cta">
                        {resetError ? (
                          <button type="button" className="button" onClick={handleRequestNewLink}>
                            <Text className="text" text="Request a new link" />
                            <i className="bi bi-arrow-right icon" aria-hidden="true" />
                          </button>
                        ) : (
                          <button
                            type="submit"
                            className={`button${canSubmitPassword ? '' : ' disabled'}`}
                            disabled={!canSubmitPassword}
                          >
                            <Text className="text" text="Set new password" />
                            <i className="bi bi-arrow-right icon" aria-hidden="true" />
                          </button>
                        )}
                      </div>
                    </form>

                    <Divider variant="styled" className="auth-divider" />

                    <div className="footer">
                      <Link className="link" to="/login">
                        <Text text="Back to sign in" />
                      </Link>
                    </div>
                  </div>
                ) : (
                  <div className="wrapper sent-view">
                    <div className="heading">
                      <div className="heading-copy">
                        <Text className="title" text="Password updated" />
                        <Text className="subtitle" text="You can now sign in with your new password." />
                      </div>
                    </div>

                    <Divider variant="styled" className="auth-divider" />

                    <div className="footer">
                      <Link className="link" to="/login">
                        <Text text="Go to sign in" />
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <LanguagePicker languages={languages} currentLanguage={currentLanguage} onChange={onChangeLanguage} />
        </div>
      </div>
    </ResetPassword>
  );
};

export default PageResetPassword;
